import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Droplets, Flower2, Sprout } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const BLOOMS = ["#F472B6", "#FBBF24", "#A78BFA", "#F87171", "#60A5FA", "#FB923C"];
const SKY: Record<string, string> = {
  spring: "from-[#E8F3FB] via-[#FDF2F6] to-[#F0F7E9]",
  summer: "from-[#DDF0FA] via-[#FFF6DD] to-[#F3F8E6]",
  autumn: "from-[#FCEBD8] via-[#FFF4E6] to-[#F6EBDD]",
  winter: "from-[#E6EEF7] via-[#F3F6FA] to-[#EEF2F6]",
};

function Plant({ stage, color, droop, i, visitor }: { stage: number; color: number; droop: number; i: number; visitor?: string | null }) {
  const h = [0, 10, 22, 34, 46, 56, 64, 70][stage];
  const tilt = droop ? Math.min(32, 10 + droop * 8) * (i % 2 ? 1 : -1) : 0;
  const bloom = BLOOMS[color];
  return (
    <div className="relative flex flex-col items-center" data-testid={`garden-plant-${i}`} data-stage={stage} data-droop={droop ? "yes" : "no"}>
      {visitor && <Visitor kind={visitor} />}
      <svg viewBox="0 0 60 90" className={`h-24 w-16 overflow-visible sm:h-28 sm:w-20 ${droop ? "saturate-50" : ""}`}>
        <g className={droop ? "" : "garden-sway"} style={{ transformOrigin: "30px 82px", transform: `rotate(${tilt}deg)`, animationDelay: `${-i * 0.7}s` }}>
          {stage === 1 && <ellipse cx="30" cy="78" rx="6" ry="4" fill="#8B5E3C" />}
          {stage >= 2 && <path d={`M30 82 Q${28 + (stage % 2) * 4} ${82 - h / 2} 30 ${82 - h}`} stroke="#4E9F5A" strokeWidth="3" fill="none" strokeLinecap="round" />}
          {stage >= 2 && <path d={`M30 ${82 - Math.min(h, 14)} q-10 -4 -12 -12 q10 0 12 10`} fill="#6BBF73" />}
          {stage >= 3 && <path d={`M30 ${82 - Math.min(h, 22)} q10 -4 12 -12 q-10 0 -12 10`} fill="#6BBF73" />}
          {stage >= 5 && <path d={`M30 ${82 - h * 0.62} q-12 -3 -15 -12 q12 0 15 10`} fill="#58AE61" />}
          {stage >= 5 && <path d={`M30 ${82 - h * 0.75} q12 -3 15 -12 q-12 0 -15 10`} fill="#58AE61" />}
          {stage === 6 && <ellipse cx="30" cy={82 - h - 4} rx="5" ry="7" fill={bloom} opacity=".8" stroke="#4E9F5A" strokeWidth="1.5" />}
          {stage === 7 && (
            <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 180, damping: 10, delay: 0.2 + i * 0.05 }} style={{ transformOrigin: `30px ${82 - h - 6}px` }}>
              {Array.from({ length: 6 }, (_, k) => <ellipse key={k} cx="30" cy={82 - h - 14} rx="5.5" ry="9" fill={bloom} transform={`rotate(${k * 60} 30 ${82 - h - 6})`} />)}
              <circle cx="30" cy={82 - h - 6} r="5" fill="#FDE68A" stroke="#F59E0B" strokeWidth="1" />
            </motion.g>
          )}
        </g>
        <ellipse cx="30" cy="84" rx="16" ry="4" fill="#7A5232" opacity=".55" />
      </svg>
      <span className="text-[11px] font-semibold text-slate-500">{stage === 7 ? "Bloomed!" : stage === 1 ? "Seed" : `Day ${stage}`}</span>
    </div>
  );
}

function Visitor({ kind }: { kind: string }) {
  return (
    <motion.div className="pointer-events-none absolute -top-6 left-1/2 z-10 -ml-6" data-testid={`garden-visitor-${kind}`}
      initial={{ x: -240, y: -110, opacity: 0 }} animate={{ x: [-240, -90, 0], y: [-110, -70, 0], opacity: [0, 1, 1] }} transition={{ duration: 3.5, ease: "easeInOut" }}>
      {kind === "butterfly" ? (
        <svg viewBox="0 0 40 30" className="h-10 w-12 drop-shadow">
          <g className="visitor-flap" style={{ transformOrigin: "20px 15px" }}>
            <path d="M20 15 C10 0 0 4 4 14 C0 22 10 28 20 16Z" fill="#A78BFA" stroke="#6D28D9" strokeWidth="1" />
            <path d="M20 15 C30 0 40 4 36 14 C40 22 30 28 20 16Z" fill="#F0ABFC" stroke="#6D28D9" strokeWidth="1" />
          </g>
          <rect x="19" y="8" width="2" height="15" rx="1" fill="#3B0764" />
        </svg>
      ) : (
        <svg viewBox="0 0 44 30" className="h-10 w-14 drop-shadow">
          <ellipse cx="22" cy="18" rx="11" ry="8" fill="#60A5FA" />
          <circle cx="31" cy="12" r="6" fill="#60A5FA" /><circle cx="33" cy="11" r="1.4" fill="#0f172a" />
          <path d="M37 12 l5 1.5 -5 1.5z" fill="#F59E0B" />
          <path className="visitor-flap" style={{ transformOrigin: "20px 16px" }} d="M20 16 q-8 -12 -16 -6 q8 2 16 10z" fill="#93C5FD" />
          <path d="M18 25 l-2 4 M24 25 l1 4" stroke="#F59E0B" strokeWidth="1.5" />
        </svg>
      )}
    </motion.div>
  );
}

function WaterDrops() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-full overflow-hidden" data-testid="garden-water-drops">
      {Array.from({ length: 10 }, (_, i) => (
        <motion.span key={i} className="absolute h-3 w-2 rounded-full bg-sky-400/80" style={{ left: `${8 + i * 9}%` }}
          initial={{ top: "-10%", opacity: 0 }} animate={{ top: ["-10%", "80%"], opacity: [0, 1, 0] }} transition={{ duration: 1.6, delay: i * 0.15, repeat: 2 }} />
      ))}
    </div>
  );
}

export function CatchGarden({ kidId, name, parent }: { kidId: string; name?: string; parent?: boolean }) {
  const [g, setG] = useState<any>(null);
  const watered = useRef(false);
  useEffect(() => {
    const load = () => api.get(`/kids/${kidId}/garden`).then((r) => { watered.current = r.data.caught_today; setG(r.data); }).catch(() => {});
    load();
    const onCatch = () => { if (!watered.current) { watered.current = true; load(); } };
    window.addEventListener("cast-caught", onCatch);
    return () => window.removeEventListener("cast-caught", onCatch);
  }, [kidId]);
  if (!g) return null;
  const droop = g.missed;
  const blooms = g.plants.map((p: any, i: number) => (p.stage === 7 ? i : -1)).filter((i: number) => i >= 0);
  const landOn = blooms.length ? blooms[blooms.length - 1] : g.plants.length - 1;
  const water = async () => {
    try { const { data } = await api.post("/family/garden-cheer", { child_id: kidId }); setG(data); toast.success(`${name}'s garden is watered! They'll see it next time.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const parentMsg = !g.days ? `${name} hasn't planted a seed yet this season.` : droop ? `${name}'s garden is drooping. A cheer might help!` : g.caught_today ? `${name} caught one today, so the garden is growing.` : `${name} hasn't caught anything yet today.`;
  const msg = !g.days ? `Catch a falling ${g.season.label.split(" ")[1].toLowerCase()} to plant your first seed!`
    : droop ? "Your garden misses you! Catch one today to perk it up."
    : g.caught_today ? "Your garden got its water today. See you tomorrow!"
    : "Catch one today to water your garden.";
  return (
    <section className={`relative overflow-hidden rounded-[2rem] bg-gradient-to-b ${SKY[g.season.id]} p-5 shadow-lg ring-1 ring-white sm:p-7`} data-testid={parent ? `parent-garden-${kidId}` : "catch-garden"}>
      <div className="flex flex-wrap items-center gap-3">
        <Sprout className="h-7 w-7 text-[#4E9F5A]" />
        <h2 className={`font-semibold text-slate-800 ${parent ? "font-display text-2xl" : "text-3xl"}`}>{name ? `${name}'s` : "My"} {g.season.id} garden</h2>
        <span className="flex-1" />
        <span className="flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-sm font-semibold text-slate-700" data-testid="garden-days"><Droplets className="h-4 w-4 text-sky-500" />{g.days} garden day{g.days === 1 ? "" : "s"}</span>
        <span className="flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-sm font-semibold text-slate-700" data-testid="garden-blooms"><Flower2 className="h-4 w-4 text-pink-500" />{g.blooms} bloom{g.blooms === 1 ? "" : "s"}</span>
      </div>
      <p className={`mt-2 text-lg ${droop ? "font-semibold text-amber-700" : "text-slate-600"}`} data-testid="garden-message">{parent ? parentMsg : msg}</p>
      {g.watered_by_parent && !parent && <p className="mt-1 flex items-center gap-2 font-semibold text-sky-700" data-testid="garden-watered-note"><Droplets className="h-5 w-5" />Your grown-up watered your garden today!</p>}
      {g.visitor && <p className="mt-1 text-sm font-semibold text-violet-700" data-testid="garden-visitor-note">A {g.visitor} came to visit because {parent ? `${name} opened` : "you opened"} a story today!</p>}
      <div className="relative mt-4 rounded-2xl bg-gradient-to-b from-transparent via-transparent to-[#C9A27A]/40 px-5 pb-1 pt-2">
        {g.visitor && !g.plants.length && <Visitor kind={g.visitor} />}
        {g.watered_by_parent && <WaterDrops />}
        {g.plants.length ? (
          <div className="flex flex-wrap items-end gap-x-1 gap-y-3" data-testid="garden-plants">
            {g.plants.map((p: any, i: number) => <Plant key={i} i={i} stage={p.stage} color={p.color} droop={droop} visitor={i === landOn ? g.visitor : null} />)}
          </div>
        ) : (
          <div className="flex h-28 items-end justify-center pb-3 text-slate-400" data-testid="garden-empty"><span className="rounded-full bg-white/70 px-4 py-1.5 text-sm font-semibold">Empty soil, waiting for a seed</span></div>
        )}
        <div className="mt-1 h-3 rounded-full bg-[#8B5E3C]/50" />
      </div>
      {parent && (
        <button onClick={water} disabled={g.watered_by_parent} data-testid={`garden-water-${kidId}`}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-sky-600 px-5 py-2 text-sm font-semibold text-white shadow transition-transform hover:scale-105 disabled:scale-100 disabled:bg-sky-200 disabled:text-sky-800">
          <Droplets className="h-4 w-4" />{g.watered_by_parent ? `You watered ${name}'s garden today` : `Water ${name}'s garden`}
        </button>
      )}
    </section>
  );
}
